import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Home, LogOut, User } from 'lucide-react';
import KeycloakService from 'security/KeycloakService';
import { useAuth } from 'hooks/useAuth';
import { useUsersContext } from 'hooks/useUsersContext';
import { roleLabel } from 'helpers/roles';
import { FormDialog } from 'components/globals/dialog/FormDialog';
import { OverviewSummaryUserForm } from 'components/user/OverviewSummary';
import { IUserResquestBody } from 'interfaces/user/IUser';
import { useClickAway } from 'ui/hooks/use-click-away';
import { cn } from 'ui/lib/utils';

const menuItem =
	'text-foreground hover:bg-accent focus-visible:outline-ring flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm focus-visible:outline-2 focus-visible:-outline-offset-2';

/** Menú del usuario: datos de la sesión, "Mi perfil", "Inicio" y "Cerrar sesión". */
export const UserMenu = () => {
	const auth = useAuth();
	const usersContext = useUsersContext();

	const [open, setOpen] = useState(false);
	const [showProfileModal, setShowProfileModal] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const close = useCallback(() => setOpen(false), []);
	useClickAway(containerRef, close);

	const firstName = KeycloakService.getFirstName() ?? '';
	const lastName = KeycloakService.getLastName() ?? '';
	const fullName = `${firstName} ${lastName}`.trim() || 'Usuario';
	const { roles } = auth;
	// /users/profile devuelve la dependencia con `id` y `type` (el nombre largo); `patrimonial_dependence` no siempre viene
	const dependence =
		auth.user?.patrimonial_dependence?.patrimonial_dependence ?? auth.user?.patrimonial_dependence?.type;

	const onSubmitProfile = async (entity: IUserResquestBody | undefined) => {
		if (!entity) return;
		const result = await usersContext?.onHandleTryUpdatePersonData(entity);
		if (result) setShowProfileModal(false);
	};

	return (
		<>
			{showProfileModal && (
				<FormDialog
					show={showProfileModal}
					title="Mi Perfil"
					content={
						<OverviewSummaryUserForm
							entity={{
								id: 0,
								first_name: firstName,
								last_name: lastName,
								email: KeycloakService.getUserEmail() || '',
								fullName,
								role_id: 0,
								role_name: '',
								is_active: true,
								group_id: 0,
								patrimonial_dependence_id: auth.user?.patrimonial_dependence?.id || 0,
								patrimonial_dependence: auth.user?.patrimonial_dependence
							}}
							handleSubmit={(entity) => {
								onSubmitProfile(entity);
							}}
							handleCancel={() => setShowProfileModal(false)}
						/>
					}
					actions={<></>}
					handleClose={() => setShowProfileModal(false)}
				/>
			)}

			<div
				ref={containerRef}
				className="relative"
				onKeyDown={(event) => {
					if (event.key === 'Escape' && open) {
						close();
						triggerRef.current?.focus();
					}
				}}>
				<button
					ref={triggerRef}
					type="button"
					aria-haspopup="menu"
					aria-expanded={open}
					onClick={() => setOpen((value) => !value)}
					className="text-foreground hover:bg-accent focus-visible:outline-ring flex h-9 cursor-pointer items-center gap-2 rounded-sm px-2 text-sm transition-colors focus-visible:outline-2">
					<User
						size={18}
						aria-hidden="true"
					/>
					<span className="hidden max-w-48 truncate sm:inline">{fullName}</span>
					<ChevronDown
						size={14}
						aria-hidden="true"
						className={cn('transition-transform duration-200', open && 'rotate-180')}
					/>
				</button>

				{open && (
					<div
						role="menu"
						aria-label="Menú de usuario"
						className="bg-card text-card-foreground border-border absolute right-0 z-40 mt-1 w-72 rounded-sm border shadow-lg">
						<div className="border-border border-b px-3 py-3 text-sm">
							<p className="font-semibold">{fullName}</p>
							<p className="text-muted-foreground mt-1">
								{roles.length > 0 ? roles.map(roleLabel).join(', ') : 'Sin rol asignado'}
							</p>
							<p className="text-muted-foreground mt-1">{dependence || 'Sin dependencia asignada'}</p>
						</div>
						<Link
							to="/home"
							role="menuitem"
							onClick={close}
							className={menuItem}>
							<Home
								size={16}
								aria-hidden="true"
							/>
							Inicio
						</Link>
						<button
							type="button"
							role="menuitem"
							onClick={() => {
								close();
								setShowProfileModal(true);
							}}
							className={menuItem}>
							<User
								size={16}
								aria-hidden="true"
							/>
							Mi perfil
						</button>
						<button
							type="button"
							role="menuitem"
							onClick={() => KeycloakService.logout()}
							className={cn(menuItem, 'border-border border-t')}>
							<LogOut
								size={16}
								aria-hidden="true"
							/>
							Cerrar sesión
						</button>
					</div>
				)}
			</div>
		</>
	);
};
